
import React from 'react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import VerifiedBadge from '@/components/common/VerifiedBadge';
import { isTemporaryUsername, getDisplayNameFallback } from '@/utils/authFlow';

interface ProfileNameDisplayProps {
  displayName: string;
  username: string;
  isAdmin?: boolean;
  isPartner?: boolean;
  isPremium?: boolean;
  isSpecialUsername?: boolean;
  registrationNumber?: number;
  tagCode?: string;
  tagEmote?: string;
}

const ProfileNameDisplay = ({ 
  displayName, 
  username, 
  isAdmin, 
  isPartner,
  isPremium,
  isSpecialUsername,
  registrationNumber,
  tagCode,
  tagEmote,
}: ProfileNameDisplayProps) => {
  const getBadgeText = () => {
    if (username === "ghost") {
      return "founder";
    }
    return registrationNumber ? `#${registrationNumber}` : "early access";
  };

  // Use fallbacks for display name and username to handle null or "anonymous" values
  const displayedName = displayName || getDisplayNameFallback({ username } as any);
  const isTemporary = isTemporaryUsername(username);

  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <h1 className="text-2xl md:text-3xl font-bold">
          {displayedName}
        </h1>
        {(isSpecialUsername || username === 'ghost' || registrationNumber || true) && (
          <Badge 
            variant="secondary" 
            className={cn(
              "bg-gradient-to-br from-[#7E69AB]/30 to-[#9b87f5]/40", // Refined soft purple gradient
              "px-3 py-1 rounded-full shadow-sm border border-white/10",
              "text-xs font-semibold tracking-wider",
              isAdmin 
                ? "from-[#F97316]/30 to-[#FEF7CD]/30 text-[#F97316]" 
                : "text-white/90 hover:text-white",
              "transition-all duration-300 hover:scale-105 hover:shadow-md",
              "backdrop-blur-sm" // Add slight blur effect
            )}
          >
            {getBadgeText()}
          </Badge>
        )}
      </div>
      <div className="inline-flex items-center gap-2">
        <span className="text-sm font-medium text-white">@{username}</span>
        {isAdmin && (
          <VerifiedBadge type="admin" size={18} className="inline-block" shadow={false} />
        )}
        {isPartner && (
          <VerifiedBadge type="partner" size={18} className="inline-block" shadow={false} />
        )}
        {isPremium && !isAdmin && !isPartner && (
          <VerifiedBadge type="premium" size={18} className="inline-block" shadow={false} />
        )}
        {/* Show tag pill if provided */}
        {tagCode && (
          <span className="inline-flex items-center gap-1 rounded-md border px-2 py-0.5 text-[12px] leading-none">
            {tagEmote && (
              <img
                src={`https://cdn.jsdelivr.net/gh/twitter/twemoji@latest/assets/72x72/${Array.from(tagEmote).map(c => c.codePointAt(0)!.toString(16)).join('-')}.png`}
                alt={tagEmote}
                className="h-4 w-4"
                style={{ imageRendering: 'pixelated' as any }}
              />
            )}
            <span className="font-mono tracking-widest">{tagCode}</span>
          </span>
        )}
      </div>
    </div>
  );
};

export default ProfileNameDisplay;
